// Range bar: lower/marker/upper. Bounds from nearest open-order prices
// bracketing current price — matches legacy updateLivePnl range bar logic.
// Visual is delegated to the shared RangeBar which matches legacy .range-bar.
import type { Order, GridConfig } from "@/types/api";
import RangeBar from "@/components/common/range-bar";

interface GridRangeBarProps {
  grid: GridConfig;
  orders: Order[];
  livePrice: number | null;
}

function computeBounds(
  orders: Order[],
  price: number,
  grid: GridConfig
): { lower: number; upper: number } {
  const openPrices = orders
    .filter((o) => o.status === "open" || o.status === "pending")
    .map((o) => o.price)
    .sort((a, b) => a - b);

  let lower: number | null = null;
  let upper: number | null = null;
  for (const p of openPrices) {
    if (p <= price) lower = p;
    else { upper = p; break; }
  }

  // No order exists past the current price on a given side → pin that side
  // to the current price so the marker sits at the edge. Reflects "marker is
  // at the boundary because nothing's beyond". Previously we'd extrapolate
  // via grid step, which made the marker float mid-bar even when the price
  // had broken past all live orders on that side.
  if (lower != null && upper == null) upper = price;
  else if (upper != null && lower == null) lower = price;
  else if (lower == null && upper == null) {
    // No open orders at all — fall back to the configured grid range so the
    // bar still renders something coherent.
    lower = grid.lower_price;
    upper = grid.upper_price;
  }

  return { lower: lower!, upper: upper! };
}

export default function GridRangeBar({ grid, orders, livePrice }: GridRangeBarProps) {
  if (!livePrice) return null;

  const { lower, upper } = computeBounds(orders, livePrice, grid);

  return (
    <RangeBar
      symbol={grid.symbol}
      lower={lower}
      upper={upper}
      current={livePrice}
    />
  );
}
